import { Bell } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes, useCallback, useId, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sheet } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { useNotifications, useRefreshNotificationsIfStale } from '../api/notifications-api';
import { useBellStream } from '../api/use-bell-stream';
import { BellPanel } from './bell-panel';

/** Tailwind's `sm`: the popover from here up, a bottom sheet below (AC2.3). */
const SM_QUERY = '(min-width: 640px)';

/**
 * The staff notification bell in the top bar (spec R1, R2).
 *
 * Mount it only for staff acting in a tenant: rendering it is what fetches the
 * snapshot and joins the live stream (AC1.1). The badge is Needs action plus
 * unread Updates, hidden at 0 and capped at "9+"; the label says the count.
 * Opening it re-reads a snapshot older than 30 s (AC2.4).
 */
export function NotificationBell() {
  const { t } = useTranslation();
  const query = useNotifications(true);
  const connection = useBellStream();
  const refreshIfStale = useRefreshNotificationsIfStale();
  const wide = useMinWidth();
  const [open, setOpen] = useState(false);
  const titleId = useId();

  const total = query.data ? query.data.needsAction.count + query.data.updates.unreadCount : 0;
  const label =
    total === 0
      ? t('notifications.bell.labelNone')
      : total > 9
        ? t('notifications.bell.labelCapped')
        : t('notifications.bell.label', { count: total });

  const onOpenChange = (next: boolean): void => {
    setOpen(next);
    // A first read still in flight needs no second one.
    if (next && query.isSuccess) refreshIfStale();
  };
  const close = useCallback(() => setOpen(false), []);

  const panel = (inSheet: boolean) => (
    <BellPanel
      snapshot={query.data}
      isPending={query.isPending}
      isError={query.isError}
      onRetry={() => void query.refetch()}
      connection={connection}
      onNavigate={close}
      titleId={titleId}
      inSheet={inSheet}
    />
  );

  if (!wide) {
    return (
      <>
        <BellTrigger
          aria-label={label}
          aria-haspopup="dialog"
          aria-expanded={open}
          total={total}
          capped={t('notifications.bell.badgeCapped')}
          open={open}
          onClick={() => onOpenChange(true)}
        />
        <Sheet open={open} onClose={close} side="bottom" title={t('notifications.title')}>
          {panel(true)}
        </Sheet>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <BellTrigger
          aria-label={label}
          aria-haspopup="dialog"
          total={total}
          capped={t('notifications.bell.badgeCapped')}
          open={open}
        />
      </PopoverTrigger>
      <PopoverContent aria-labelledby={titleId}>{panel(false)}</PopoverContent>
    </Popover>
  );
}

/** The `size-9 rounded-xl border` bell with its danger badge (AC1.2, AC1.3). */
const BellTrigger = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { total: number; capped: string; open: boolean }
>(({ total, capped, open, className, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    className={cn(
      'relative grid size-9 shrink-0 place-items-center rounded-xl border bg-card text-secondary-foreground outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring',
      open && 'border-primary ring-[3px] ring-primary/15',
      className,
    )}
    {...props}
  >
    <Bell className="size-[18px]" aria-hidden />
    {total > 0 ? (
      <span
        aria-hidden
        className="absolute -right-1.5 -top-1.5 min-w-[18px] rounded-full border-2 border-card bg-destructive px-[5px] py-px text-center text-[10px] font-bold leading-[14px] text-destructive-foreground tabular-nums"
      >
        {total > 9 ? capped : total}
      </span>
    ) : null}
  </button>
));
BellTrigger.displayName = 'BellTrigger';

/**
 * Whether the viewport is at least `sm`. Without `matchMedia` (tests, very old
 * browsers) it answers yes: the popover is the default container.
 */
function useMinWidth(): boolean {
  return useSyncExternalStore(subscribeToSm, readSm, () => true);
}

function subscribeToSm(onChange: () => void): () => void {
  if (typeof window.matchMedia !== 'function') return () => {};
  const mql = window.matchMedia(SM_QUERY);
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
}

function readSm(): boolean {
  return typeof window.matchMedia !== 'function' || window.matchMedia(SM_QUERY).matches;
}
